import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import { styled } from '@mui/material/styles'
import { icons, onImgError } from 'constants/images'
import { colors } from 'constants/theme'
import { useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

export const ButtonDelete = styled(Box)(({ theme }) => ({
    position: 'absolute',
    cursor: 'pointer',
    right: 8,
    bottom: 8,
    zIndex: 5,
    backgroundColor: 'transparent !important',
    padding: 0,
    img: {
        width: '24px !important',
        height: '24px !important'
    },
}))

type UploadImageProps = {
    setValue: (files: any) => void
    pathFile: string
    canDelete?: boolean
    onDelete?: () => void
    height?: string | number
    width?: string | number
    maxWidth?: number
    maxHeight?: number
}

const baseStyle = {
    flex: 1,
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: colors.lightGray,
    borderStyle: 'dashed',
    backgroundColor: colors.white,
    color: colors.disabledLightGray,
    outline: 'none',
    transition: 'border .24s ease-in-out',
    cursor: 'pointer'
}

export default function UploadImage({
    setValue,
    pathFile,
    canDelete,
    onDelete,
    height = "350px",
    width = "100%",
    maxWidth,
    maxHeight,
    ...props
}: UploadImageProps) {
    const { t } = useTranslation()

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        accept: ['.jpeg', '.jpg', '.png', '.webp'],
        multiple: false,
        onDrop: (acceptedFiles) => {
            setValue(acceptedFiles)
        }
    })

    const style: any = useMemo(
        () => ({
            ...baseStyle
        }),
        []
    )

    return (
        <FormControl size="small" margin="none" fullWidth sx={{ position: 'relative' }}>
            <Box
                {...getRootProps({ isDragActive, isDragAccept, isDragReject, style })}
                justifyContent="center"
                alignItems="center"
                display="flex"
                sx={{
                    height,
                    minHeight: height,
                    maxHeight: maxHeight ? `${maxHeight}px` : height,
                    overflow: 'hidden',
                    position: 'relative',
                    ...style,
                }}
            >
                <input {...getInputProps()} />
                {!pathFile ? (
                    <Box
                        sx={{
                            width: '100%',
                            height: '80%',
                            minHeight: height,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'auto',
                        }}
                    >
                        <img
                            src={icons.iconAddPhoto}
                            alt="iconAddPhoto"
                            style={{
                                width: '70px',
                                height: '70px',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }}
                        />
                        <Box sx={{ pb: 0, mb: 0, mt: 2, textAlign: 'center', width: '100%' }}>
                            {t('UPLOAD_IMAGE')}
                        </Box>
                    </Box>
                ) : (
                    <Box
                        component="img"
                        src={pathFile}
                        alt="photoUpload"
                        width="100%"
                        height="100%"
                        onError={onImgError}
                        sx={{
                            objectFit: 'contain',
                            maxWidth: maxWidth ? `${maxWidth}px` : '100%',
                            maxHeight: maxHeight ? `${maxHeight}px` : height,
                            justifyContent: 'center',
                            alignItems: 'center',
                            display: 'flex',
                        }}
                    />
                )}
            </Box>
            {(pathFile && canDelete) && (
                <ButtonDelete
                    aria-label="delete"
                    onClick={onDelete}
                    className="btn-delete"
                >
                    <img src={icons.btnTrash} alt="trash" />
                </ButtonDelete>
            )}
        </FormControl>
    )
}
